import * as t from 'io-ts';
import {snakeCase} from 'lodash';

import {makeApiType, makeEnumRuntimeType} from './apiTypes';

/*
 * Runtime type.
 */

export enum IntegrationSchemaTypesEnum {
  OBJECT = 'object',
  ARRAY = 'array',
  NUMBER = 'number',
  STRING = 'string',
}

export enum IntegrationTypeCategoriesEnum {
  ANALYTICS = 'analytics', // Legacy
  ANALYTICS_REPORTING = 'analytics_reporting',
  BILLING_INVOICING = 'billing_invoicing',
  BOTS = 'bots', // Legacy
  BOTS_WORKFLOW_AUTOMATION = 'bots_workflow_automation',
  CHANNEL = 'channel', // Legacy
  CHANNELS_MESSAGING = 'channels_messaging',
  CLOUD_STORAGE = 'cloud_storage', // Legacy
  COMMUNICATION = 'communication', // Legacy
  CONTACTS = 'contacts', // Legacy
  CRM_ECOMMERCE = 'crm_ecommerce',
  CRM_SALES_MANAGEMENT = 'crm_sales_management', // Legacy
  CUSTOMER_SUPPORT = 'customer_support', // Legacy
  DATA = 'data', // Legacy
  DATA_PRIVACY = 'data_privacy',
  DATA_SYNC_ENRICHMENT = 'data_sync_enrichment',
  DEVELOPER_TOOLS = 'developer_tools', // Legacy
  ECOMMERCE = 'ecommerce', // Legacy
  KNOWLEDGE_CONTENT = 'knowledge_content',
  ISSUE_TRACKING = 'issue_tracking', // Legacy
  MARKETING = 'marketing', // Legacy
  MESSAGING = 'messaging', // Legacy
  PAYMENTS_INVOICING = 'payments_invoicing', // Legacy
  PRODUCTIVITY = 'productivity',
  PROJECT_MANAGEMENT = 'project_management', // Legacy
  PROJECT_MANAGEMENT_ISSUE_TRACKING = 'project_management_issue_tracking',
  SURVEYS_FEEDBACK = 'surveys_feedback',
  VOICE = 'voice', // Legacy
  VOICE_TELEPHONY = 'voice_telephony',
  ESSENTIALS = 'essentials',
}

export enum IntegrationTypesSupportedTypesEnum {
  CHANNEL = 'channel',
  OTHER = 'other',
  PLUGIN = 'plugin',
}

const CommonSchemaDirty = t.type({
  default: t.union([t.string, t.record(t.string, t.any)]),
  enum: t.union([t.readonlyArray(t.string), t.undefined]),
  enumDescriptions: t.union([t.readonlyArray(t.string), t.undefined]),
  enumTitles: t.union([t.readonlyArray(t.string), t.undefined]),
  properties: t.union([t.record(t.string, t.any), t.undefined]),
  required: t.union([t.readonlyArray(t.string), t.undefined]),
  type: t.union([makeEnumRuntimeType(IntegrationSchemaTypesEnum), t.undefined]),
  format: t.union([t.string, t.undefined]),
  additionalProperties: t.union([t.boolean, t.undefined]),
  items: t.union([t.any, t.undefined]),
});

export const RIntegrationTypeCategory = makeEnumRuntimeType(IntegrationTypeCategoriesEnum);
export const RIntegrationTypesSupportedType = makeEnumRuntimeType(IntegrationTypesSupportedTypesEnum);

const RIntegrationTypePublisher = t.readonly(
  t.type({
    name: t.string,
    privacyPolicy: t.string,
    support: t.string,
    website: t.string,
  }),
  'integrationTypesPublisher',
);

const RIntegrationTypeDirty = t.readonly(
  t.type({
    id: t.string,
    name: t.string,
    description: t.string,
    external: t.boolean,
    categories: t.readonlyArray(RIntegrationTypeCategory),
    notificationEmails: t.readonlyArray(t.string),
    searchKeywords: t.readonlyArray(t.string),
    updatedAt: t.Integer,
    popularity: t.number,
    oauthConnected: t.boolean,
    supportedTypes: t.readonlyArray(RIntegrationTypesSupportedType),
    launchDate: t.number,
    screenshots: t.readonlyArray(t.string),
    longDescription: t.union([t.undefined, t.string]),
    publisher: t.union([RIntegrationTypePublisher, t.undefined]),
    isConfigurationRequiredToEnable: t.boolean,
    hasConfiguration: t.boolean,
    plans: t.readonlyArray(t.string),
  }),
  'integrationType',
);

const RIntegrationTypesWithSchemaDirty = t.readonly(
  t.type({
    commonSchema: t.record(t.string, CommonSchemaDirty),
    integrationTypes: t.record(t.string, t.any),
  }),
  'integrationTypesWithSchema',
);

/*
 * Type.
 */

export interface CommonSchema extends t.TypeOf<typeof CommonSchemaDirty> {}
export interface IntegrationTypesWithSchemaModel extends t.TypeOf<typeof RIntegrationTypesWithSchemaDirty> {}
export interface IntegrationTypeModel extends t.TypeOf<typeof RIntegrationTypeDirty> {}
export interface IntegrationTypePublisherModel extends t.TypeOf<typeof RIntegrationTypePublisher> {}

export const RIntegrationTypesWithSchema = t.clean<IntegrationTypesWithSchemaModel>(
  RIntegrationTypesWithSchemaDirty as any,
);
export const RIntegrationType = t.clean<IntegrationTypeModel>(RIntegrationTypeDirty as any);
export const RIntegrationTypes = t.readonlyArray(RIntegrationType);

/*
 * IO methods.
 */

export function parseIntegrationTypesWithSchema(src: any): IntegrationTypesWithSchemaModel {
  return {
    commonSchema: src.common_schema,
    integrationTypes: src.integration_types,
  };
}

function parseIntegrationTypePublisher(src: any): IntegrationTypePublisherModel | undefined {
  if (!src) {
    return undefined;
  }
  return {
    name: src.name,
    privacyPolicy: src.privacyPolicy,
    support: src.support,
    website: src.website,
  };
}

export function parseIntegrationType(src: any): IntegrationTypeModel {
  return {
    id: src.id,
    name: src.name,
    description: src.description,
    external: src.external || Boolean(!src.custom_plugin?.url && src.publisher?.name !== 'Front'),
    categories: parseIntegrationTypesCategories(src.categories || []),
    notificationEmails: src.notificationEmails || [],
    searchKeywords: (src.searchKeyWords || []).map((c: any) => String(c).toLowerCase()),
    supportedTypes: parseSupportedTypes(src),
    launchDate: src.launchDate ? new Date(src.launchDate).getTime() : 0,
    popularity: src.popularity || 0,
    screenshots: src.screenshots || [],
    oauthConnected: Boolean(src.oauth_connected),
    longDescription: undefined,
    publisher: parseIntegrationTypePublisher(src.publisher),
    isConfigurationRequiredToEnable: Boolean(src.requires_configuration),
    hasConfiguration: Boolean(src.has_configuration),
    plans: src.plans || [],
    // TODO: If we want to support live updating integration_types we will need to pass back an updated_at
    //       value but currently we do not so that can not be supported.
    updatedAt: 0,
  };
}

function parseSupportedTypes(src: any) {
  const supportedTypes = (src.type || [])
    .map((type: any) => snakeCase(String(type.toLowerCase())))
    // Make sure the support type is a valid one and ignore the rest.
    .filter(RIntegrationTypesSupportedType.is);

  // Default to displaying as Other if we have no valid types.
  if (supportedTypes.length === 0) {
    return [IntegrationTypesSupportedTypesEnum.OTHER];
  }
  return supportedTypes;
}

export function parseIntegrationTypesCategories(src: any[]) {
  const parsedCategories = src
    .map((c: any) => snakeCase(String(c)))
    // Make sure the category is a valid one and ignore the rest.
    .filter(RIntegrationTypeCategory.is);

  if (parsedCategories.length === 0) {
    return [IntegrationTypeCategoriesEnum.ESSENTIALS];
  }
  return parsedCategories;
}

export function parseIntegrationTypes(src: any): ReadonlyArray<IntegrationTypeModel> {
  return src.integration_types.map(parseIntegrationType);
}

/*
 * API type.
 */

export const ApiIntegrationTypesWithSchema = makeApiType(
  'ApiIntegrationTypesWithSchema',
  RIntegrationTypesWithSchema,
  parseIntegrationTypesWithSchema,
);
export const ApiIntegrationTypes = makeApiType('integrationTypes', RIntegrationTypes, parseIntegrationTypes);
